import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Badge } from '../components/ui/Badge';
import {
  Shield,
  ShieldAlert,
  Plus,
  Trash2,
  RefreshCw,
  Clock,
  Ban,
  Activity,
  Search,
  X,
  AlertTriangle
} from 'lucide-react';
import { apiService } from '../services/api';
import { useToast } from '../components/ui/Toast';
import ConfirmModal from '../components/ui/ConfirmModal';

interface BlacklistedIP {
  ip: string;
  type: 'permanent' | 'temporary';
  blockedAt: string | null;
  expiresAt: string | null;
  reason?: string;
}

interface BlacklistStats {
  totalBlocked: number;
  permanent: number;
  temporary: number;
  connections?: {
    totalIPs: number;
    totalConnections: number;
    blockedIPs: number;
  };
}

interface NotFoundStats {
  windowMs: number;
  total404: number;
  topIPs: Array<{ ip: string; count: number }>;
  topPaths: Array<{ path: string; count: number }>;
}

const SecurityManagement: React.FC = () => {
  const { showToast } = useToast();
  const [blacklist, setBlacklist] = useState<{ permanent: BlacklistedIP[]; temporary: BlacklistedIP[] }>({
    permanent: [],
    temporary: []
  });
  const [stats, setStats] = useState<BlacklistStats>({
    totalBlocked: 0,
    permanent: 0,
    temporary: 0
  });
  const [notFoundStats, setNotFoundStats] = useState<NotFoundStats>({
    windowMs: 0,
    total404: 0,
    topIPs: [],
    topPaths: []
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'permanent' | 'temporary'>('all');
  
  // Estados para modais/formulários
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTempBlockModal, setShowTempBlockModal] = useState(false);
  const [newIP, setNewIP] = useState('');
  const [blockMinutes, setBlockMinutes] = useState(15);
  const [blockReason, setBlockReason] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ipToDelete, setIpToDelete] = useState<string | null>(null);

  // Carregar blacklist
  const loadBlacklist = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getBlacklist();
      if (response.success) {
        setBlacklist(response.data.blacklist);
        setStats(response.data.stats);
      }
    } catch (error: any) {
      console.error('Erro ao carregar blacklist:', error);
      showToast({ 
        type: 'error', 
        title: 'Erro', 
        message: 'Erro ao carregar lista de IPs bloqueados' 
      });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Carregar estatísticas
  const loadStats = useCallback(async () => {
    try {
      const response = await apiService.getSecurityStats();
      if (response.success) {
        setStats(prev => ({
          ...prev,
          totalBlocked: response.data.blacklist?.total ?? prev.totalBlocked,
          permanent: response.data.blacklist?.permanent ?? prev.permanent,
          temporary: response.data.blacklist?.temporary ?? prev.temporary,
          connections: response.data.connections
        }));

        if (response.data.notFound) {
          setNotFoundStats({
            windowMs: response.data.notFound.windowMs,
            total404: response.data.notFound.total404,
            topIPs: response.data.notFound.topIPs || [],
            topPaths: response.data.notFound.topPaths || []
          });
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  useEffect(() => {
    loadBlacklist();
    // Atualizar estatísticas a cada 30 segundos
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, [loadBlacklist, loadStats]);

  // Adicionar IP à blacklist permanente
  const handleAddToBlacklist = async () => {
    if (!newIP.trim()) {
      showToast({ 
        type: 'error', 
        title: 'Erro', 
        message: 'Por favor, informe um endereço IP' 
      });
      return;
    }

    try {
      const response = await apiService.addToBlacklist(
        newIP.trim(),
        blockReason.trim() || undefined
      );

      if (response.success) {
        showToast({ 
          type: 'success', 
          title: 'Sucesso!', 
          message: 'IP adicionado à blacklist permanente' 
        });
        setShowAddModal(false);
        setNewIP('');
        setBlockReason('');
        loadBlacklist();
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Erro ao adicionar IP à blacklist';
      showToast({ 
        type: 'error', 
        title: 'Erro', 
        message: errorMsg 
      });
    }
  };

  // Bloquear IP temporariamente
  const handleTempBlock = async () => {
    if (!newIP.trim()) {
      showToast({ 
        type: 'error', 
        title: 'Erro', 
        message: 'Por favor, informe um endereço IP' 
      });
      return;
    }

    if (blockMinutes < 1 || blockMinutes > 1440) {
      showToast({ 
        type: 'error', 
        title: 'Erro', 
        message: 'Minutos devem estar entre 1 e 1440 (24 horas)' 
      });
      return;
    }

    try {
      const response = await apiService.tempBlockIP(
        newIP.trim(),
        blockMinutes,
        blockReason.trim() || undefined
      );

      if (response.success) {
        showToast({ 
          type: 'success', 
          title: 'Sucesso!', 
          message: `IP bloqueado temporariamente por ${blockMinutes} minutos` 
        });
        setShowTempBlockModal(false);
        setNewIP('');
        setBlockMinutes(15);
        setBlockReason('');
        loadBlacklist();
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Erro ao bloquear IP temporariamente';
      showToast({ 
        type: 'error', 
        title: 'Erro', 
        message: errorMsg 
      });
    }
  };

  // Remover IP da blacklist
  const handleRemoveFromBlacklist = (ip: string) => {
    setIpToDelete(ip);
    setShowDeleteConfirm(true);
  };

  const confirmRemoveFromBlacklist = async () => {
    if (!ipToDelete) return;

    try {
      const response = await apiService.removeFromBlacklist(ipToDelete);

      if (response.success) {
        showToast({ 
          type: 'success', 
          title: 'Sucesso!', 
          message: response.message || 'IP desbloqueado com sucesso' 
        });
        loadBlacklist();
        loadStats();
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Erro ao remover IP da blacklist';
      showToast({ 
        type: 'error', 
        title: 'Erro', 
        message: errorMsg 
      });
    } finally {
      setShowDeleteConfirm(false);
      setIpToDelete(null);
    }
  };

  // Filtrar IPs
  const filteredIPs = {
    permanent: blacklist.permanent.filter(ip => 
      ip.ip.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    temporary: blacklist.temporary.filter(ip => 
      ip.ip.toLowerCase().includes(searchTerm.toLowerCase())
    )
  };

  // Calcular tempo restante para bloqueios temporários
  const getTimeRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expirado';
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}min`;
    return `${minutes}min`;
  };

  const allFilteredIPs = [
    ...filteredIPs.permanent.map(ip => ({ ...ip, displayType: 'permanent' as const })),
    ...filteredIPs.temporary.map(ip => ({ ...ip, displayType: 'temporary' as const }))
  ];

  const finalFilteredIPs = filterType === 'all' 
    ? allFilteredIPs 
    : filterType === 'permanent'
    ? filteredIPs.permanent.map(ip => ({ ...ip, displayType: 'permanent' as const }))
    : filteredIPs.temporary.map(ip => ({ ...ip, displayType: 'temporary' as const }));

  const blockedIpSet = useMemo(() => {
    const set = new Set<string>();
    blacklist.permanent.forEach((item) => set.add(item.ip));
    blacklist.temporary.forEach((item) => set.add(item.ip));
    return set;
  }, [blacklist]);

  const isIpListedAsBlocked = (ip: string) => blockedIpSet.has(ip);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8" />
            Gerenciamento de Segurança
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie IPs bloqueados e monitore estatísticas de segurança
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadBlacklist}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowTempBlockModal(true)}
          >
            <Clock className="w-4 h-4 mr-2" />
            Bloquear Temporariamente
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Adicionar IP
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" />
              Total Bloqueados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalBlocked}</div>
            <p className="text-xs text-muted-foreground mt-1">IPs bloqueados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Ban className="w-4 h-4" />
              Permanentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.permanent}</div>
            <p className="text-xs text-muted-foreground mt-1">Bloqueios permanentes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Temporários
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.temporary}</div>
            <p className="text-xs text-muted-foreground mt-1">Bloqueios temporários</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Conexões
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.connections?.totalConnections || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.connections?.totalIPs || 0} IPs ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Erros 404
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{notFoundStats.total404}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Top scanners na janela
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Observabilidade 404 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Top IPs por 404</CardTitle>
            <CardDescription>Os IPs que mais bateram em rotas inexistentes</CardDescription>
          </CardHeader>
          <CardContent>
            {notFoundStats.topIPs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem dados na janela.</p>
            ) : (
              <div className="space-y-2">
                {notFoundStats.topIPs.map((item) => (
                  <div
                    key={item.ip}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors gap-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <code className="text-sm font-mono font-semibold truncate">{item.ip}</code>
                      {isIpListedAsBlocked(item.ip) && (
                        <Badge variant="destructive" className="shrink-0">Bloqueado</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="secondary">{item.count}× 404</Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveFromBlacklist(item.ip)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Desbloquear este IP"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Desbloquear
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Paths por 404</CardTitle>
            <CardDescription>Quais endpoints inexistentes estão sendo varridos</CardDescription>
          </CardHeader>
          <CardContent>
            {notFoundStats.topPaths.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem dados na janela.</p>
            ) : (
              <div className="space-y-2">
                {notFoundStats.topPaths.map((item) => (
                  <div
                    key={item.path}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <code className="text-sm font-mono font-semibold">{item.path}</code>
                    <Badge variant="secondary">{item.count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de IPs Bloqueados</CardTitle>
          <CardDescription>
            IPs bloqueados permanentemente ou temporariamente. Use &quot;Desbloquear&quot; para liberar o acesso.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Buscar por IP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterType === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('all')}
              >
                Todos
              </Button>
              <Button
                variant={filterType === 'permanent' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('permanent')}
              >
                Permanentes
              </Button>
              <Button
                variant={filterType === 'temporary' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('temporary')}
              >
                Temporários
              </Button>
            </div>
          </div>

          {/* Lista de IPs */}
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-muted-foreground">Carregando...</p>
            </div>
          ) : finalFilteredIPs.length === 0 ? (
            <div className="text-center py-8 space-y-3">
              <Shield className="w-12 h-12 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Nenhum IP encontrado na blacklist' : 'Nenhum IP na blacklist no momento'}
              </p>
              {!searchTerm && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground max-w-md mx-auto">
                    Use &quot;Desbloquear&quot; na lista &quot;Top IPs por 404&quot; acima, ou informe o IP abaixo:
                  </p>
                  <div className="flex gap-2 max-w-md mx-auto">
                    <Input
                      placeholder="Ex: 192.168.1.100"
                      value={newIP}
                      onChange={(e) => setNewIP(e.target.value)}
                    />
                    <Button
                      variant="outline"
                      disabled={!newIP.trim()}
                      onClick={() => handleRemoveFromBlacklist(newIP.trim())}
                      className="shrink-0 text-red-600 hover:text-red-700"
                    >
                      Desbloquear
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {finalFilteredIPs.map((item) => {
                const timeRemaining = item.type === 'temporary' ? getTimeRemaining(item.expiresAt) : null;
                const isExpired = item.type === 'temporary' && timeRemaining === 'Expirado';

                return (
                  <div
                    key={item.ip}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-sm font-mono font-semibold">{item.ip}</code>
                          <Badge variant={item.type === 'permanent' ? 'destructive' : 'secondary'}>
                            {item.type === 'permanent' ? (
                              <>
                                <Ban className="w-3 h-3 mr-1" />
                                Permanente
                              </>
                            ) : (
                              <>
                                <Clock className="w-3 h-3 mr-1" />
                                Temporário
                              </>
                            )}
                          </Badge>
                          {isExpired && (
                            <Badge variant="outline" className="text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Expirado
                            </Badge>
                          )}
                        </div>
                        {item.type === 'temporary' && timeRemaining && !isExpired && (
                          <p className="text-xs text-muted-foreground">
                            Expira em: {timeRemaining}
                          </p>
                        )}
                        {item.reason && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Motivo: {item.reason}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveFromBlacklist(item.ip)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                      title="Desbloquear IP"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Desbloquear
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal: Adicionar IP Permanente */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Adicionar IP à Blacklist Permanente</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewIP('');
                    setBlockReason('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription>
                O IP será bloqueado permanentemente até remoção manual
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="ip">Endereço IP *</Label>
                <Input
                  id="ip"
                  placeholder="192.168.1.100"
                  value={newIP}
                  onChange={(e) => setNewIP(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Formato IPv4 ou IPv6
                </p>
              </div>
              <div>
                <Label htmlFor="reason">Motivo (opcional)</Label>
                <Input
                  id="reason"
                  placeholder="Ex: Tentativas repetidas de acesso não autorizado"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewIP('');
                    setBlockReason('');
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={handleAddToBlacklist}>
                  <Ban className="w-4 h-4 mr-2" />
                  Bloquear Permanente
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Bloquear Temporariamente */}
      {showTempBlockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Bloquear IP Temporariamente</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowTempBlockModal(false);
                    setNewIP('');
                    setBlockMinutes(15);
                    setBlockReason('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription>
                O IP será bloqueado por um período específico
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="temp-ip">Endereço IP *</Label>
                <Input
                  id="temp-ip"
                  placeholder="192.168.1.100"
                  value={newIP}
                  onChange={(e) => setNewIP(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="minutes">Duração (minutos) *</Label>
                <Input
                  id="minutes"
                  type="number"
                  min="1"
                  max="1440"
                  value={blockMinutes}
                  onChange={(e) => setBlockMinutes(parseInt(e.target.value) || 15)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Entre 1 e 1440 minutos (24 horas)
                </p>
              </div>
              <div>
                <Label htmlFor="temp-reason">Motivo (opcional)</Label>
                <Input
                  id="temp-reason"
                  placeholder="Ex: Teste de bloqueio temporário"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTempBlockModal(false);
                    setNewIP('');
                    setBlockMinutes(15);
                    setBlockReason('');
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={handleTempBlock}>
                  <Clock className="w-4 h-4 mr-2" />
                  Bloquear Temporariamente
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Confirmação de Remoção */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setIpToDelete(null);
        }}
        onConfirm={confirmRemoveFromBlacklist}
        title="Desbloquear IP"
        message={`Desbloquear o IP ${ipToDelete}? Ele voltará a acessar a API imediatamente.`}
        confirmText="Desbloquear"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default SecurityManagement;
